// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import {
  Download as DownloadIcon,
  ExpandMore,
} from '@/constants/hardfin-standard-icons';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Link,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback, useState } from 'react';

interface TemplateDownloadProps {
  onBack: () => void;
  onNext: () => void;
}

interface Field {
  label: string;
  csvHeader: string;
}

interface FieldCategory {
  id: string;
  label: string;
  fields: Field[];
}

const fieldCategories: FieldCategory[] = [
  {
    id: 'advancedCosts',
    label: 'Include advanced cost breakdown',
    fields: [
      { label: 'Material cost - decimal', csvHeader: 'Bill of materials' },
      { label: 'Direct labor - decimal', csvHeader: 'Direct labor' },
      {
        label: 'Allocated indirect - decimal',
        csvHeader: 'Allocated indirect',
      },
      { label: 'Freight inbound - decimal', csvHeader: 'Freight inbound' },
      { label: 'Freight outbound - decimal', csvHeader: 'Freight outbound' },
      { label: 'Installation - decimal', csvHeader: 'Installation' },
      { label: 'Tax - decimal', csvHeader: 'Tax' },
      { label: 'Tariffs - decimal', csvHeader: 'Tariffs' },
      { label: 'Interest - decimal', csvHeader: 'Interest' },
    ],
  },
  {
    id: 'sale',
    label: 'Include sale record',
    fields: [
      { label: 'Sale date - date (YYYY-MM-DD)', csvHeader: 'Sale date' },
      { label: 'Sale price - decimal (optional)', csvHeader: 'Sale price' },
      {
        label: 'Sale customer - company name (required)',
        csvHeader: 'Sale customer',
      },
    ],
  },
  {
    id: 'scrap',
    label: 'Include scrap detail',
    fields: [
      { label: 'Scrap date - date (YYYY-MM-DD)', csvHeader: 'Scrap date' },
      { label: 'Scrap reason - text (optional)', csvHeader: 'Scrap reason' },
      { label: 'Scrap value - decimal (optional)', csvHeader: 'Scrap value' },
    ],
  },
];

type CategoryToggles = Record<string, boolean>;

export function TemplateDownload({ onBack, onNext }: TemplateDownloadProps) {
  const { user } = useCurrentUser();
  const orgDefaultCurrency = user.organization.defaultCurrency;

  const [categoryToggles, setCategoryToggles] = useState<CategoryToggles>(
    () => {
      const initial: CategoryToggles = {};
      fieldCategories.forEach((cat) => {
        initial[cat.id] = false;
      });
      return initial;
    }
  );

  const handleToggleChange = (categoryId: string) => {
    setCategoryToggles((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleDownloadTemplate = useCallback(() => {
    const headers = [
      'Asset serial',
      'Product name',
      'Product SKU',
      'Inventory date',
      'Initial location',
      'Initial location customer',
      'Initial location address',
      'Description',
      'Status',
      'In service date',
      'Useful life',
      // Accounting currency applies to every cost column; importer strips the "(default ...)" hint
      `Accounting currency (default ${orgDefaultCurrency})`,
    ];

    // Simple cost basis precedes salvage value; only present when advanced costs is off
    if (!categoryToggles['advancedCosts']) {
      headers.push('Simple cost basis');
    }
    headers.push('Salvage value');

    // Add headers from toggled-on categories
    fieldCategories.forEach((category) => {
      if (categoryToggles[category.id]) {
        headers.push(...category.fields.map((f) => f.csvHeader));
      }
    });

    const csvContent = headers.join(',') + '\n';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `asset_creation_template_${DateTime.now().toFormat('yyyy-MM-dd_HHmm')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [categoryToggles, orgDefaultCurrency]);

  return (
    <Card variant={'outlined'}>
      <CardContent>
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: (theme) => theme.palette.grey.A100,
            borderRadius: '10px',
            marginBottom: '10px',
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Template for creating new assets
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Required fields
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 12 }}>
            <li>
              <Typography variant="body2">Asset serial - text</Typography>
            </li>
            <li>
              <Typography variant="body2">
                Inventory date - date (YYYY-MM-DD)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Initial location - location name
              </Typography>
            </li>
          </ul>
          <Typography variant="subtitle2" gutterBottom>
            Optional fields
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 0 }}>
            <li>
              <Typography variant="body2">
                Product name - text (at least one of name or SKU required)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">Product SKU - text</Typography>
            </li>
            <li>
              <Typography variant="body2">
                Initial location customer - customer name
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Initial location address - location address
              </Typography>
            </li>
            <li>
              <Typography variant="body2">Description - text</Typography>
            </li>
            <li>
              <Typography variant="body2">
                Status - (FUNCTIONAL or NON_FUNCTIONAL, defaults to functional)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                In service date - date (YYYY-MM-DD)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Useful life - integer (months)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Accounting currency -{' '}
                <Tooltip
                  title={
                    <>
                      Must be a valid ISO 4217 currency code
                      <br />
                      Examples:
                      <br />
                      USD - United States dollar
                      <br />
                      CAD - Canadian dollar
                      <br />
                      EUR - euro
                      <br />
                      GBP - British pound
                      <br />
                      <Link
                        href="https://en.wikipedia.org/wiki/ISO_4217"
                        target="_blank"
                        rel="noopener"
                        color="inherit"
                        underline="always"
                      >
                        See the full list of codes on Wikipedia
                      </Link>
                    </>
                  }
                  arrow
                >
                  <span
                    style={{
                      textDecorationLine: 'underline',
                      textDecorationStyle: 'dotted',
                    }}
                  >
                    ISO code
                  </span>
                </Tooltip>{' '}
                (required when a row includes a monetary amount, otherwise
                defaults to the item's accounting currency or{' '}
                {orgDefaultCurrency})
              </Typography>
            </li>
            {!categoryToggles['advancedCosts'] && (
              <li>
                <Typography variant="body2">
                  Simple cost basis - decimal
                </Typography>
              </li>
            )}
            <li>
              <Typography variant="body2">Salvage value - decimal</Typography>
            </li>
          </ul>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Optional field categories
            </Typography>

            {fieldCategories.map((category) => (
              <Accordion
                key={category.id}
                disableGutters
                elevation={0}
                sx={{
                  backgroundColor: 'transparent',
                  '&:before': { display: 'none' },
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '4px !important',
                  mb: 1,
                  '&:last-of-type': { mb: 0 },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    minHeight: 48,
                    '& .MuiAccordionSummary-content': {
                      margin: 0,
                      alignItems: 'center',
                    },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={categoryToggles[category.id]}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggleChange(category.id);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        size="small"
                      />
                    }
                    label={
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography variant="body2">
                          {category.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({category.fields.length} fields)
                        </Typography>
                      </Box>
                    }
                    onClick={(e) => e.stopPropagation()}
                    sx={{ m: 0 }}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <ul
                    style={{
                      paddingLeft: '24px',
                      marginTop: 0,
                      marginBottom: 0,
                    }}
                  >
                    {category.fields.map((field) => (
                      <li key={field.csvHeader}>
                        <Typography variant="body2">{field.label}</Typography>
                      </li>
                    ))}
                  </ul>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Important information about the template</AlertTitle>
          <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
            <li>Serials must be unique</li>
            <li>
              Product can be identified by name or SKU (at least one required)
            </li>
            <li>
              Locations and customers are created automatically if they don't
              exist
            </li>
            <li>
              Date format must be YYYY-MM-DD (ex: '2020-06-25' for June 25,
              2020)
            </li>
          </ul>
        </Alert>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={onBack}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
          <Button variant="contained" onClick={onNext}>
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
